import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from './components/ui/toaster';
import Header from './components/Header';
import GlobeSection from './components/GlobeSection';
import RunPanel from './components/RunPanel';
import CountryCubes from './components/CountryCubes';
import ActivityChart from './components/ActivityChart';
import LiveScrapers from './components/LiveScrapers';
import LiveCrons from './components/LiveCrons';
import CSVSecondary from './components/CSVSecondary';
import NewCrawlForm from './components/NewCrawlForm';
import RunsTimeline from './components/RunsTimeline';
import { runInfo, monitorStats } from './mock/mock';
import { Bot, Cog, Zap, FileText } from 'lucide-react';
import { LiveProvider } from './context/LiveContext';

const MiniKPI = ({ icon, label, value, sub, accent = 'white' }) => {
  const accents = { cyan: 'text-cyan-300', pink: 'text-pink-300', white: 'text-white', amber: 'text-amber-300', emerald: 'text-emerald-300' };
  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3">
      <div className="flex items-center gap-1.5 text-white/50 mb-1">
        {icon}
        <p className="text-[9px] font-mono uppercase tracking-widest">{label}</p>
      </div>
      <p className={`text-xl font-bold font-mono tabular-nums leading-tight ${accents[accent]}`}>{value}</p>
      <p className="text-[10px] text-white/45 mt-0.5">{sub}</p>
    </div>
  );
};

const Dashboard = () => {
  return (
    <div className="min-h-screen bg-[#040609] text-white relative">
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.08) 0%, transparent 60%)' }} />
        <div className="absolute top-1/3 -right-40 w-[600px] h-[600px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(236,72,153,0.06) 0%, transparent 60%)' }} />
        <div className="absolute bottom-0 left-1/3 w-[500px] h-[500px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.05) 0%, transparent 60%)' }} />
      </div>

      <Header duration={runInfo.lastRunDuration} />

      <main className="max-w-[1500px] mx-auto px-5 py-5 relative space-y-5">
        {/* Row 1: Globe (radar piloté) + Run panel */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
          <GlobeSection />
          <RunPanel />
        </div>

        {/* Row 2: KPI mini */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          <MiniKPI icon={<Zap className="h-3 w-3" />} label="Produits" value="45" sub="base cumulée" accent="cyan" />
          <MiniKPI icon={<Zap className="h-3 w-3" />} label="Proxy 30j" value="0.01" sub="Go estimés" />
          <MiniKPI icon={<Zap className="h-3 w-3" />} label="Runs" value="27" sub="historique" />
          <MiniKPI icon={<FileText className="h-3 w-3" />} label="Fichiers CSV" value="15" sub="15 jours" />
          <MiniKPI icon={<Bot className="h-3 w-3" />} label="Crons" value={monitorStats.crons.count} sub="tous OK" accent="emerald" />
          <MiniKPI icon={<Cog className="h-3 w-3" />} label="Scrapers" value="3" sub="en cours" accent="pink" />
          <MiniKPI icon={<Zap className="h-3 w-3" />} label="Batch P.P" value="5/247" sub="18 succès" accent="pink" />
          <MiniKPI icon={<Zap className="h-3 w-3" />} label="Incidents" value="1" sub="dernières 24h" accent="amber" />
        </div>

        {/* Row 3: Country cubes 3D + Activity chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <CountryCubes />
          <ActivityChart />
        </div>

        {/* Row 4: Live Scrapers + Live Crons (deux fenêtres actives cote à cote) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <LiveScrapers />
          <LiveCrons />
        </div>

        {/* Row 5: CSV secondary (moins prioritaire) */}
        <CSVSecondary />

        {/* Row 6: New crawl + Runs history */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-4 pb-10">
          <NewCrawlForm />
          <RunsTimeline />
        </div>
      </main>

      <Toaster />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <LiveProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Dashboard />} />
          </Routes>
        </BrowserRouter>
      </LiveProvider>
    </div>
  );
}

export default App;
